import { DatePicker } from "@/components/DatePicker";
import { SearchableSelect } from "@/components/SearchableSelect";
import { SensitiveField } from "@/components/SensitiveField";
import {
  alphaNumSpace,
  businessStartDateRules,
  formatEIN,
  formatSSN,
} from "@/lib/formatters";
import { searchMcc, MCC_BY_CODE, formatMccLabel } from "@/lib/mcc";
import { storeTypes } from "@/lib/storeTypes";
import type { BusinessForm, FieldSetter } from "./types";
import { FieldWrap } from "./FieldWrap";
import { type FieldHelpDisplay } from "./FieldHelp";
import { FormField, formFieldDescribedBy } from "./FormField";
import { TF } from "./TF";

export function BusinessDetailsFields({
  biz,
  setBizField,
  fieldError,
  required,
  hideSensitive = false,
  helpDisplay = "tooltip",
}: {
  biz: BusinessForm;
  setBizField: FieldSetter<BusinessForm>;
  fieldError: (field: string) => string | undefined;
  required: boolean;
  // Skips the taxID field. Set when this component is reused somewhere
  // that only has a masked last-4 on file (never the raw value) --
  // ApplicationDetail's EditApplicationModal, notably -- so there's no
  // real value to prefill or safely overwrite.
  hideSensitive?: boolean;
  /** Operator forms: tooltip (default). Merchant intake: inline. */
  helpDisplay?: FieldHelpDisplay;
}) {
  const taxIdError = fieldError("taxID");
  const mccError = fieldError("mcc");
  const startDateError = fieldError("businessStartDate");
  const taxIdIsEin = biz.taxFilingMethod !== "SSN";
  const formatTaxId = taxIdIsEin ? formatEIN : formatSSN;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TF
          id="dbaName"
          label="DBA name"
          value={biz.dbaName}
          onChange={(v) => setBizField("dbaName", v)}
          format="alphanum"
          hint="Defaults to the legal business name if blank."
          maxLength={200}
        />
        <FormField
          id="taxFilingName"
          label="Tax filing name"
          hint="Defaults to legal name if blank."
          helpDisplay={helpDisplay}
        >
          <input
            id="taxFilingName"
            type="text"
            className="input"
            value={biz.taxFilingName}
            onChange={(e) =>
              setBizField("taxFilingName", alphaNumSpace(e.target.value))
            }
            maxLength={200}
            autoComplete="off"
            aria-describedby={formFieldDescribedBy("taxFilingName", {
              hint: "Defaults to legal name if blank.",
              helpDisplay,
            })}
          />
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <FormField
          id="taxFilingMethod"
          label="Tax filing method"
          required={required}
          helpDisplay={helpDisplay}
        >
          <select
            id="taxFilingMethod"
            className="input"
            value={biz.taxFilingMethod}
            onChange={(e) => {
              setBizField("taxFilingMethod", e.target.value);
              setBizField("taxID", "");
            }}
            aria-required={required || undefined}
          >
            <option value="EIN">EIN</option>
            <option value="SSN">SSN</option>
          </select>
        </FormField>
        {!hideSensitive && (
          <FormField
            id="taxID"
            label={taxIdIsEin ? "EIN" : "SSN"}
            sensitive
            required={required}
            error={taxIdError}
            helpDisplay={helpDisplay}
          >
            <SensitiveField
              id="taxID"
              value={formatTaxId(biz.taxID)}
              onChange={(v) => setBizField("taxID", formatTaxId(v))}
              maxLength={taxIdIsEin ? 10 : 11}
              inputMode="numeric"
              placeholder={taxIdIsEin ? "12-3456789" : "123-45-6789"}
              ariaInvalid={!!taxIdError}
              ariaDescribedBy={formFieldDescribedBy("taxID", {
                error: taxIdError,
                helpDisplay,
              })}
            />
          </FormField>
        )}
        <FieldWrap
          id="mcc"
          label="Merchant Category (MCC)"
          required={required}
          error={mccError}
        >
          <SearchableSelect
            id="mcc"
            value={biz.mcc}
            options={[]}
            onChange={(v) => {
              setBizField("mcc", v);
              setBizField("storeType", "");
            }}
            ariaInvalid={!!mccError}
            ariaDescribedBy={mccError ? "mcc-err" : undefined}
            selectedLabel={
              biz.mcc && MCC_BY_CODE[biz.mcc]
                ? formatMccLabel(MCC_BY_CODE[biz.mcc])
                : undefined
            }
            customFilter={(q) =>
              searchMcc(q).map((m) => ({
                value: m.code,
                label: formatMccLabel(m),
                hint: m.description,
              }))
            }
            renderItem={(opt) => (
              <div>
                <div className="font-mono text-xs text-brand-muted">
                  {opt.value}
                </div>
                <div>{opt.hint ?? opt.label}</div>
              </div>
            )}
            placeholder="Type to search…"
          />
          {biz.mcc && MCC_BY_CODE[biz.mcc] && (
            <p className="hint">
              Selected: {formatMccLabel(MCC_BY_CODE[biz.mcc])}
            </p>
          )}
        </FieldWrap>
      </div>
      {biz.mcc && storeTypes.some((s) => s.mccCode === biz.mcc) && (
        <FieldWrap id="storeType" label="Store type">
          <select
            id="storeType"
            className="input"
            value={biz.storeType}
            onChange={(e) => setBizField("storeType", e.target.value)}
          >
            <option value="">Select one…</option>
            {storeTypes
              .filter((s) => s.mccCode === biz.mcc)
              .map((s) => (
                <option key={s.value} value={s.value}>
                  {s.name}
                </option>
              ))}
          </select>
        </FieldWrap>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          id="businessStartDate"
          label="Business start date"
          required={required}
          error={startDateError}
          helpDisplay={helpDisplay}
          width="date"
        >
          <DatePicker
            id="businessStartDate"
            value={biz.businessStartDate}
            onChange={(v) => setBizField("businessStartDate", v)}
            rules={businessStartDateRules()}
            error={startDateError}
            required={required}
            ariaInvalid={!!startDateError}
            ariaDescribedBy={formFieldDescribedBy("businessStartDate", {
              error: startDateError,
              helpDisplay,
            })}
          />
        </FormField>
        <TF
          id="website"
          label="Website"
          value={biz.website}
          onChange={(v) => setBizField("website", v)}
          maxLength={200}
          placeholder="https://..."
          error={fieldError("website")}
        />
      </div>
      <TF
        id="businessDescription"
        label="Business description"
        value={biz.businessDescription}
        onChange={(v) => setBizField("businessDescription", v)}
        maxLength={500}
      />
    </>
  );
}
